import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useAlert } from "react-alert";

import onEnvironmentChange from "../../../hooks/on-environment-change-by-user";
import useCurrentEnvironment from "../../../hooks/use-environment";
import Navigation from "../../../Navigation";
import { getFeatureFlag, makeRequest } from "../../../util";
import ImportFormDialog from "../../Components/ImportFormDialog";
import SideBar from "../../Components/SideBar";
import Playground from "../../Containers/PlayGround";
import HomePageLayout from "../../layouts/HomePageLayout";

const useStyles = makeStyles(() => ({
  navigation: {
    paddingTop: 24,
    paddingBottom: 24,
    height: "100%",
  },
}));

/**
 * Home Component
 *
 * Can have routing logic to render different Layouts/pages
 * for different routes,  devices etc.
 */
const Home = () => {
  const alert = useAlert();
  // Sidebar State to toggle drawer
  const [showDrawer, _setShowDrawer] = useState(true);

  // State to toggle Import Data Dialog
  const [showImportDialog, setShowImportDialog] = useState(false);
  const currentEnvironment = useCurrentEnvironment();

  const [isTableMetadataLoading, setIsTableMetadataLoading] = useState(true);
  const [tableMetadata, setTableMetadata] = useState([]);

  const proteusEnabled =
    !currentEnvironment.loaded ||
    !!getFeatureFlag(currentEnvironment.project, "sqlTransformations");

  const disabledMessage =
    !proteusEnabled &&
    `Contact support@freshpaint.io to enable this feature for the ${currentEnvironment.project.name} project. Provide your project ID: ${currentEnvironment.project.ID}.`;

  function fetchTables() {
    if (!currentEnvironment.loaded) {
      setIsTableMetadataLoading(true);
      setTableMetadata([]);
      return;
    }
    if (!proteusEnabled) {
      setIsTableMetadataLoading(false);
      setTableMetadata([]);
      return;
    }

    setIsTableMetadataLoading(true);
    makeRequest("get_sql_metadata", {})
      .then((resp) => {
        setIsTableMetadataLoading(false);
        setTableMetadata(resp.data);
      })
      .catch((e) => {
        setIsTableMetadataLoading(false);
        alert.error("Something went wrong. Unable to fetch tables.");
        console.log(e);
        Sentry.captureException(e);
      });
  }

  // Fetch the environment's tables when the page loads
  useEffect(fetchTables, [currentEnvironment.loaded]);

  // When the user picks a different environment, re-fetch the tables
  onEnvironmentChange(fetchTables);

  const toggleImportDialogState = () => {
    setShowImportDialog((val) => !val);
  };

  const handleImportDialogSuccess = () => {
    setShowImportDialog((val) => !val);
  };

  return (
    <HomePageLayout
      sideBar={
        <SideBar
          isLoading={isTableMetadataLoading}
          showDrawer={showDrawer}
          items={tableMetadata}
        />
      }
    >
      <Playground disabledMessage={disabledMessage} />
      <ImportFormDialog
        showDialog={showImportDialog}
        handleCancelAction={toggleImportDialogState}
        handleSuccessAction={handleImportDialogSuccess}
      />
    </HomePageLayout>
  );
};

function HomePageWithNavigation() {
  const classes = useStyles();
  return (
    <Navigation className={clsx(classes.navigation)}>
      <Home />
    </Navigation>
  );
}

export default HomePageWithNavigation;
